import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryFilters = [
  { id: 'all', label: 'All' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'amenities', label: 'Amenities' },
  { id: 'experiences', label: 'Experiences' },
];

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.category === activeFilter));
    }
  }, [activeFilter, images]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
      setFilteredImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Gallery | Rudragarh Resort, Jaisalmer</title>
        <meta name="description" content="Photos of Rudragarh Resort — rooms, desert landscapes, dunes, pool, dining, and experiences at our boutique property near Jaisalmer, Rajasthan." />
        <meta property="og:title" content="Gallery | Rudragarh Resort" />
        <meta property="og:description" content="See Rudragarh through photos — desert stays, dunes, pool, and unforgettable moments in Rajasthan." />
        <meta property="og:url" content="https://www.rudragarh.com/gallery" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gallery | Rudragarh Resort" />
        <meta name="twitter:description" content="See Rudragarh through photos — desert stays, dunes, pool, and unforgettable moments in Rajasthan." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/gallery" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[40vh] flex items-center justify-center" data-testid="gallery-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/resort-pool-night.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="overline">Visual Journey</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              <span className="text-gold-500">Gallery</span>
            </h1>
            <p className="text-foreground/70 max-w-xl mx-auto">
              A glimpse into the world of Rudragarh
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-20 z-40 bg-void/95 backdrop-blur-md border-b border-gold-500/10 py-4">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex items-center justify-center gap-4 overflow-x-auto pb-2">
            {categoryFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all whitespace-nowrap ${
                  activeFilter === filter.id
                    ? 'bg-gold-500 text-black'
                    : 'text-foreground/60 hover:text-gold-500 border border-transparent hover:border-gold-500/30'
                }`}
                data-testid={`gallery-filter-${filter.id}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="aspect-square bg-void-subtle animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`relative overflow-hidden cursor-pointer group ${
                    index % 5 === 0 ? 'md:col-span-2 md:row-span-2' : ''
                  }`}
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <div className={`${index % 5 === 0 ? 'aspect-square' : 'aspect-square'}`}>
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-void/0 group-hover:bg-void/60 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center p-4">
                        <p className="font-heading text-lg">{image.title}</p>
                        <p className="text-gold-500 text-xs uppercase tracking-wider mt-1">{image.category}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-void/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-foreground hover:text-gold-500 transition-colors"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
              <p className="font-heading text-xl">{selectedImage.title}</p>
              <p className="text-gold-500 text-sm uppercase tracking-wider mt-1">{selectedImage.category}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
